import {
  Coins,
  FileDown,
  LassoSelect,
  Map as MapIcon,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { CoreShowcase } from "./core-card";

interface ClipStep {
  /** Short name for the module line under the card copy. */
  label: string;
  /** The step as it reads on the rail. */
  name: string;
  /** What that step produces, shown in mono beneath the name. */
  note: string;
  icon: LucideIcon;
}

/** A clip job, in the order the rail walks it. */
const CLIP_STEPS: ClipStep[] = [
  {
    label: "Browse",
    name: "Browse Data",
    note: "Building Footprint, Cadastre, Address",
    icon: MapIcon,
  },
  { label: "Boundary", name: "Clip Boundary", note: "LGA Parramatta", icon: LassoSelect },
  { label: "Quote", name: "Clip Quote", note: "1,424 credits", icon: Coins },
  { label: "Extract", name: "Clip Extract", note: "Shapefile, 64.2 MB", icon: FileDown },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Clip's showcase plate: the four steps of a clip job down a vertical rail. The
 * active step — driven by CoreCard's shared rotation `active` — moves down the
 * rail, filling each node it reaches, so the plate walks Browse → Boundary →
 * Quote → Extract and loops back to a fresh job. Every step stays listed; the
 * active one is brought forward and shows its result in Clip's teal.
 */
const ClipRail = ({ active }: { active: number }) => {
  const reduced = prefersReducedMotion();
  const count = CLIP_STEPS.length;
  // Under reduced motion the plate never rotates, so show the job run through.
  const progress = reduced ? count - 1 : active;

  return (
    <div className="absolute inset-0 flex flex-col px-5 py-4">
      <ol className="relative flex flex-1 flex-col">
        {CLIP_STEPS.map((step, i) => {
          const reached = i <= progress;
          const isActive = !reduced && i === active;
          const Icon = step.icon;

          return (
            <li
              key={step.label}
              data-testid="home-core-clip-panel"
              data-active={isActive}
              className="relative flex flex-1 items-center gap-3"
            >
              {/* Rail segment into this node from the one above. */}
              {i > 0 && (
                <span
                  aria-hidden
                  style={{ top: "-50%" }}
                  className={cn(
                    "absolute left-4 h-full w-px -translate-x-1/2 transition-colors duration-500",
                    reached ? "bg-primary" : "bg-border",
                  )}
                />
              )}
              <span
                className={cn(
                  "relative z-10 grid size-8 shrink-0 place-items-center rounded-lg border transition-all duration-500",
                  reached
                    ? "border-transparent bg-primary text-background"
                    : "border-border bg-card text-muted-foreground",
                  isActive && "scale-105 shadow-lg",
                )}
              >
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    "truncate text-sm font-medium transition-colors",
                    isActive
                      ? "text-foreground"
                      : reached
                        ? "text-foreground/80"
                        : "text-muted-foreground",
                  )}
                >
                  {step.name}
                </div>
                <div
                  className={cn(
                    "font-mono text-[0.6875rem] leading-tight transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {step.note}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export const CLIP_SHOWCASE: CoreShowcase = {
  labels: CLIP_STEPS.map((step) => step.label),
  render: (active) => <ClipRail active={active} />,
};
